/** Dates and sizes as the mock-ups write them (French, Paris time). */

const TIME_ZONE = 'Europe/Paris';

const dayKey = new Intl.DateTimeFormat('fr-FR', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' });
const weekdayDayMonth = new Intl.DateTimeFormat('fr-FR', { timeZone: TIME_ZONE, weekday: 'short', day: 'numeric', month: 'short' });
const dayMonth = new Intl.DateTimeFormat('fr-FR', { timeZone: TIME_ZONE, day: 'numeric', month: 'short' });
const dayMonthYear = new Intl.DateTimeFormat('fr-FR', { timeZone: TIME_ZONE, day: 'numeric', month: 'short', year: 'numeric' });
const longDate = new Intl.DateTimeFormat('fr-FR', { timeZone: TIME_ZONE, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const time = new Intl.DateTimeFormat('fr-FR', { timeZone: TIME_ZONE, hour: '2-digit', minute: '2-digit' });
const monthLong = new Intl.DateTimeFormat('fr-FR', { timeZone: TIME_ZONE, month: 'long' });

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** A `date` column (YYYY-MM-DD) read as a calendar day, not shifted by the time zone. */
function fromDay(value: string): Date {
  return new Date(`${value}T12:00:00Z`);
}

/** « Il y a 3 h », « Hier », « Lun. 13 oct. » (column « Mise à jour » of screen 01). */
export function formatRelative(iso: string, now = new Date()): string {
  const date = new Date(iso);
  const minutes = Math.floor((now.getTime() - date.getTime()) / 60_000);
  if (minutes < 1) return "À l'instant";
  const sameDay = dayKey.format(date) === dayKey.format(now);
  if (sameDay && minutes < 60) return `Il y a ${minutes} min`;
  if (sameDay) return `Il y a ${Math.floor(minutes / 60)} h`;
  const yesterday = new Date(now.getTime() - 24 * 3600_000);
  if (dayKey.format(date) === dayKey.format(yesterday)) return 'Hier';
  return capitalize(weekdayDayMonth.format(date));
}

/** « 13 oct. 09:12 » */
export function formatDayTime(iso: string): string {
  const date = new Date(iso);
  return `${dayMonth.format(date)} ${time.format(date)}`;
}

/** « 22 oct. » — accepts a timestamp or a `date` column. */
export function formatDayMonth(value: string): string {
  return dayMonth.format(value.length === 10 ? fromDay(value) : new Date(value));
}

/** « 13 oct. 2026 » */
export function formatDate(value: string): string {
  return dayMonthYear.format(value.length === 10 ? fromDay(value) : new Date(value));
}

/** « jeudi 9 octobre 2026 » */
export function formatLongDate(value: string): string {
  return longDate.format(value.length === 10 ? fromDay(value) : new Date(value));
}

/** « octobre » — month of `now`, for « Depuis le 1er octobre ». */
export function currentMonthName(now = new Date()): string {
  return monthLong.format(now);
}

/** « 412 Ko » */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mo`;
}

/** « Camille D. » */
/** Set from the session: « Camille Se. » where « Camille S. » would be shared by two people. */
let shortNameHints: Record<string, string> = {};
export function setShortNameHints(hints: Record<string, string>): void {
  shortNameHints = hints;
}

export function shortName(person: { id?: string; firstName: string; lastName: string }): string {
  const hint = person.id ? shortNameHints[person.id] : undefined;
  if (hint) return hint;
  const initial = person.lastName.charAt(0);
  return initial ? `${person.firstName} ${initial}.` : person.firstName;
}

export function fullName(person: { firstName: string; lastName: string }): string {
  return `${person.firstName} ${person.lastName}`.trim();
}
